import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, FileText, ExternalLink, Save, Loader2, X, Microscope, Users, Sparkles, Hash, Link2, StickyNote, Palette } from "lucide-react";
import { toast } from "sonner";
import { tagColorClasses } from "./Sidebar";
import CitationStyleSelector from "./CitationStyleSelector";
import ColorPicker from "./ColorPicker";
import { paletteToken, DEFAULT_NODE_COLOR } from "@/lib/palette";
import { getCitationFormatted, getCitationBibtex, updateCitation, CITATION_STYLES } from "@/lib/api";

const SECTIONS = [
  { key: "methodology", label: "Methodology", icon: Microscope, placeholder: "Study design, methods, data collection…" },
  { key: "population", label: "Population", icon: Users, placeholder: "Sample size, participants, demographics…" },
  { key: "main_findings", label: "Main findings", icon: Sparkles, placeholder: "Key results, conclusions, effect sizes…" },
];

export default function CitationInspector({ citation, folders, tags, allCitations = [], onClose, onUpdate, style = "apa7", keywordColor = () => "forest", inline = false }) {
  const [formatted, setFormatted] = useState("");
  const [localStyle, setLocalStyle] = useState(style);
  const [bibtex, setBibtex] = useState("");
  const [notes, setNotes] = useState("");
  const [methodology, setMethodology] = useState("");
  const [population, setPopulation] = useState("");
  const [mainFindings, setMainFindings] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [nodeColor, setNodeColor] = useState(DEFAULT_NODE_COLOR);
  const [relatedIds, setRelatedIds] = useState([]);
  const [relatedSearch, setRelatedSearch] = useState("");
  const [folderId, setFolderId] = useState("none");
  const [tagIds, setTagIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLocalStyle(style);
  }, [style]);

  useEffect(() => {
    if (!citation) return;
    setNotes(citation.notes || "");
    setMethodology(citation.methodology || "");
    setPopulation(citation.population || "");
    setMainFindings(citation.main_findings || "");
    setKeywords(citation.keywords || []);
    setRelatedIds(citation.related_article_ids || []);
    setFolderId(citation.folder_id || "none");
    setTagIds(citation.tag_ids || []);
    setNodeColor(citation.color || DEFAULT_NODE_COLOR);
    setKeywordInput("");
    setRelatedSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citation?.id]);

  useEffect(() => {
    if (!citation?.id) return;
    setLoading(true);
    Promise.all([getCitationFormatted(citation.id, localStyle), getCitationBibtex(citation.id)])
      .then(([a, b]) => {
        setFormatted(a);
        setBibtex(b);
      })
      .finally(() => setLoading(false));
  }, [citation?.id, citation?.title, citation?.year, localStyle]);

  if (!citation) return null;

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const toggleTag = (id) =>
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addKeyword = () => {
    const k = keywordInput.trim();
    if (!k) return;
    if (keywords.map((x) => x.toLowerCase()).includes(k.toLowerCase())) {
      setKeywordInput("");
      return;
    }
    setKeywords([...keywords, k]);
    setKeywordInput("");
  };

  const removeKeyword = (k) => setKeywords(keywords.filter((x) => x !== k));

  const toggleRelated = (id) =>
    setRelatedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateCitation(citation.id, {
        notes,
        methodology,
        population,
        main_findings: mainFindings,
        keywords,
        related_article_ids: relatedIds,
        folder_id: folderId === "none" ? null : folderId,
        tag_ids: tagIds,
        color: nodeColor,
      });
      onUpdate(updated);
      toast.success("Saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sectionValues = { methodology, population, main_findings: mainFindings };
  const sectionSetters = { methodology: setMethodology, population: setPopulation, main_findings: setMainFindings };

  const availableRelated = allCitations.filter((c) => c.id !== citation.id);
  const filteredRelated = availableRelated.filter((c) => {
    if (!relatedSearch.trim()) return true;
    const q = relatedSearch.toLowerCase();
    return (
      (c.title || "").toLowerCase().includes(q) ||
      (c.authors || []).some((a) => a.toLowerCase().includes(q))
    );
  });
  const selectedRelated = availableRelated.filter((c) => relatedIds.includes(c.id));

  const Header = inline ? "div" : SheetHeader;
  const Title = inline ? "h2" : SheetTitle;
  const body = (
    <>
        <Header className={inline ? "space-y-1.5" : undefined}>
          <div className="text-xs uppercase tracking-widest text-[#7A6A8C] font-semibold">
            {citation.type} · {citation.year || "n.d."}
          </div>
          <Title className="font-serif-display text-2xl leading-tight text-[#3B2350] dark:text-[#FBEFF6] pr-6">
            {citation.title}
          </Title>
          <p className="text-sm italic text-[#5E4B70] dark:text-[#BBA9CF]">
            {(citation.authors || []).join("; ") || "Unknown author"}
          </p>
          {citation.journal && (
            <p className="text-xs text-[#7A6A8C]">
              {citation.journal}
              {citation.volume ? `, ${citation.volume}` : ""}
              {citation.issue ? `(${citation.issue})` : ""}
              {citation.pages ? `, ${citation.pages}` : ""}
            </p>
          )}
        </Header>

        <div className="mt-6 space-y-6">
          {(citation.url || citation.doi) && (
            <a
              href={citation.doi ? `https://doi.org/${citation.doi}` : citation.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#C9286F] dark:text-[#C4A1FF] hover:underline"
              data-testid="inspector-source-link"
            >
              <ExternalLink className="w-4 h-4" />
              {citation.doi ? `doi.org/${citation.doi}` : "Open source"}
            </a>
          )}

          {citation.abstract && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold">Abstract</Label>
              <p className="mt-2 text-sm text-[#5E4B70] dark:text-[#BBA9CF] leading-relaxed">{citation.abstract}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold">Formatted</Label>
                <CitationStyleSelector value={localStyle} onChange={setLocalStyle} compact />
              </div>
              <button
                data-testid="inspector-copy-apa"
                onClick={() =>
                  copy(
                    formatted.replace(/\*/g, ""),
                    CITATION_STYLES.find((s) => s.id === localStyle)?.label || "Citation"
                  )
                }
                className="text-xs text-[#C9286F] dark:text-[#C4A1FF] flex items-center gap-1 hover:underline"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="p-4 rounded-lg bg-white dark:bg-[#1E1529] border border-[#F5D0E3] dark:border-[#45325A] text-sm text-[#3B2350] dark:text-[#FBEFF6] leading-relaxed">
              {loading
                ? "…"
                : formatted.split(/\*(.+?)\*/g).map((chunk, i) => (i % 2 === 1 ? <em key={i}>{chunk}</em> : <span key={i}>{chunk}</span>))}
            </div>
          </div>

          {/* Structured research fields */}
          <div className="pt-4 border-t border-[#F5D0E3] dark:border-[#45325A] space-y-5">
            <div className="text-xs uppercase tracking-widest text-[#C9286F] dark:text-[#C4A1FF] font-semibold flex items-center gap-2">
              <StickyNote className="w-3.5 h-3.5" /> Research notes
            </div>

            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.key}>
                  <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" /> {s.label}
                  </Label>
                  <Textarea
                    data-testid={`inspector-${s.key.replace("_", "-")}-input`}
                    value={sectionValues[s.key]}
                    onChange={(e) => sectionSetters[s.key](e.target.value)}
                    placeholder={s.placeholder}
                    className="mt-1.5 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A] min-h-[110px] leading-relaxed"
                  />
                </div>
              );
            })}

            {/* Keywords */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" /> Keywords
                <span className="ml-1 normal-case tracking-normal text-xs text-[#7A6A8C] font-normal italic">
                  papers sharing keywords get linked on the map
                </span>
              </Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 rounded-lg bg-white dark:bg-[#1E1529] border border-[#F5D0E3] dark:border-[#45325A] min-h-[48px]">
                {keywords.map((k) => {
                  const tok = paletteToken(keywordColor(k));
                  return (
                    <span
                      key={k}
                      data-testid={`inspector-keyword-${k}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                      style={{ backgroundColor: tok.bg, color: tok.fg }}
                    >
                      {k}
                      <button
                        onClick={() => removeKeyword(k)}
                        className="hover:opacity-70 transition-opacity"
                        aria-label={`Remove ${k}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
                <input
                  data-testid="inspector-keyword-input"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addKeyword();
                    }
                    if (e.key === "Backspace" && !keywordInput && keywords.length) {
                      removeKeyword(keywords[keywords.length - 1]);
                    }
                  }}
                  placeholder={keywords.length ? "" : "Add a keyword and press Enter…"}
                  className="flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1"
                />
              </div>
            </div>

            {/* Related articles */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold flex items-center gap-1.5">
                <Link2 className="w-3.5 h-3.5" /> Related articles
              </Label>
              {selectedRelated.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5 mb-2">
                  {selectedRelated.map((c) => (
                    <span
                      key={c.id}
                      data-testid={`related-selected-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#FCE4F0] text-[#C9286F] dark:bg-[#4A1D3A] dark:text-[#F9A8D4] max-w-full"
                    >
                      <span className="truncate max-w-[220px]">{c.title}</span>
                      <button onClick={() => toggleRelated(c.id)} className="hover:opacity-70">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Input
                data-testid="inspector-related-search"
                value={relatedSearch}
                onChange={(e) => setRelatedSearch(e.target.value)}
                placeholder={availableRelated.length ? "Search your library to link…" : "No other citations yet"}
                disabled={availableRelated.length === 0}
                className="bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
              />
              {relatedSearch.trim() && (
                <div className="mt-1.5 border border-[#F5D0E3] dark:border-[#45325A] rounded-lg bg-white dark:bg-[#1E1529] max-h-48 overflow-y-auto">
                  {filteredRelated.length === 0 ? (
                    <div className="p-3 text-xs text-[#7A6A8C]">Nothing matches</div>
                  ) : (
                    filteredRelated.slice(0, 20).map((c) => {
                      const active = relatedIds.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          data-testid={`related-option-${c.id}`}
                          onClick={() => toggleRelated(c.id)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140] flex items-start gap-2 ${
                            active ? "bg-[#FCE4F0]/50 dark:bg-[#4A1D3A]/50" : ""
                          }`}
                        >
                          <span className="mt-0.5 text-[#C9286F] dark:text-[#C4A1FF]">
                            {active ? "✓" : "+"}
                          </span>
                          <span>
                            <span className="font-medium text-[#3B2350] dark:text-[#FBEFF6]">{c.title}</span>
                            <span className="text-[#7A6A8C] block mt-0.5">
                              {(c.authors || []).slice(0, 2).join("; ")}
                              {c.year ? ` · ${c.year}` : ""}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Own notes */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5" /> Your notes & quotes
              </Label>
              <Textarea
                data-testid="inspector-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Your reactions, quotes, questions, connections to your work…"
                className="mt-1.5 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A] min-h-[160px] leading-relaxed"
              />
            </div>
          </div>

          {/* Organize */}
          <div className="pt-4 border-t border-[#F5D0E3] dark:border-[#45325A] space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold">Project</Label>
              <Select value={folderId} onValueChange={setFolderId}>
                <SelectTrigger data-testid="inspector-folder-select" className="mt-1 bg-white dark:bg-[#1E1529]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unfiled</SelectItem>
                  {folders.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold">Tags</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.length === 0 && <span className="text-xs text-[#7A6A8C]">No tags yet</span>}
                {tags.map((t) => {
                  const active = tagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggleTag(t.id)}
                      className={`text-xs px-2 py-1 rounded-full ${tagColorClasses(t.color)} ${
                        active ? "ring-2 ring-[#C9286F] dark:ring-[#C4A1FF]" : "opacity-80"
                      }`}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5" /> Map colour
                <span className="ml-1 normal-case tracking-normal text-xs text-[#7A6A8C] font-normal italic">
                  how this paper appears on the literature map
                </span>
              </Label>
              <div className="mt-2">
                <ColorPicker value={nodeColor} onChange={setNodeColor} testIdPrefix="node-color" />
              </div>
            </div>

            <Button
              data-testid="inspector-save-button"
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </Button>
          </div>

          {/* BibTeX moved to the bottom - reference-only technical detail */}
          <div className="pt-4 border-t border-[#F5D0E3] dark:border-[#45325A]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C] font-semibold">BibTeX</Label>
              <button
                data-testid="inspector-copy-bibtex"
                onClick={() => copy(bibtex, "BibTeX")}
                className="text-xs text-[#C9286F] dark:text-[#C4A1FF] flex items-center gap-1 hover:underline"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <pre className="p-4 rounded-lg bg-[#3B2350] dark:bg-[#150F1E] text-[#FCE7F3] text-xs font-mono-scholar overflow-x-auto whitespace-pre-wrap leading-5 max-h-56">
              {loading ? "…" : bibtex}
            </pre>
          </div>
        </div>
    </>
  );

  if (inline)
    return (
      <aside
        data-testid="citation-inspector"
        className="relative w-[460px] shrink-0 h-screen overflow-y-auto border-l border-[#F5D0E3] dark:border-[#45325A] bg-white/80 dark:bg-[#2E2140] p-6 fade-slide-in"
      >
        <button
          data-testid="inspector-close-inline"
          onClick={onClose}
          aria-label="Close details"
          className="absolute right-4 top-4 w-8 h-8 grid place-items-center rounded-full text-[#5E4B70] hover:bg-[#FCE7F3] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        {body}
      </aside>
    );

  return (
    <Sheet open={!!citation} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        data-testid="citation-inspector"
        className="w-full sm:max-w-2xl overflow-y-auto bg-[#FFF5FA] dark:bg-[#3B2350] border-l border-[#F5D0E3] dark:border-[#45325A]"
      >
        {body}
      </SheetContent>
    </Sheet>
  );
}
